/**
 * 策略类提示语常量
 * 职责: 目标人群、痛点、USP、市场定位等策略相关提示语
 */
import type { TemplateType } from '../../../types/ai-analysis-types';

export const STRATEGY_PROMPTS: Partial<Record<TemplateType, string>> = {
    target_audience: `你是一位拥有 15 年经验的市场调研专家。请基于以下产品信息，构建精准的目标受众画像 (Persona)。

# 核心指令
1. **空值处理**：如果【产品类别】为空，请根据【产品名称】推断其所属类目。
2. **画像细分**：不要只给出笼统的"年轻人"或"家庭主妇"，要细分到具体的生活状态（如：追求效率的独居白领、注重健康的二胎妈妈）。

产品名称: {{product_name}}
产品类别: {{category}}

# 分析要求
1. **人口统计学特征**：年龄段、性别比例、收入水平、教育程度、居住区域。
2. **心理特征**：价值观、生活态度、兴趣爱好、消费观念。
3. **购买动机**：他们为什么需要这个产品？是为了解决问题、提升效率，还是满足情感需求？`,

    pain_points: `你是一位专注于用户体验 (UX) 的产品经理。请深入挖掘目标用户在特定场景下可能遇到的痛点和未被满足的需求。

# 核心指令
1. **空值处理**：如果【使用场景】为空，请自行构思该产品的 3 个典型高频使用场景。
2. **深度挖掘**：不要只列出表面的"不方便"，要挖掘背后的"焦虑"、"恐惧"或"渴望"。

产品名称: {{product_name}}
使用场景: {{usage_scenarios}}

# 分析要求
1. 列出 3-5 个核心痛点，按严重程度排序。
2. 对于每个痛点，分析其对用户生活造成的具体负面影响。
3. 简要说明我们的产品如何解决这些痛点。`,

    usage_scenarios: `你是一位极具想象力的场景营销专家。请发散思维，为该产品构思 5 个具体的使用场景，覆盖从常规到创新的不同维度。

# 核心指令
1. **空值处理**：如果【产品特性】为空，请基于产品名称推断其基础功能。
2. **场景多元化**：场景应包含：
   - **高频场景**：日常生活中最容易遇到的。
   - **极端场景**：产品性能的极限展示。
   - **礼品场景**：作为礼物送给他人。

产品名称: {{product_name}}
产品特性: {{product_features}}

# 分析要求
1. 每个场景都要有具体的时间、地点、人物和事件。
2. 描述用户在每个场景下的具体行为和心理活动。`,

    primary_usp: `你是一位特劳特战略定位专家。请基于竞品分析，提炼该产品的核心卖点 (Unique Selling Proposition, USP)。

# 核心指令
1. **空值处理**：如果【竞品分析】为空，请假设该产品面临市场上常见的同质化竞争，寻找差异化突破口。
2. **单一聚焦**：USP 必须聚焦于一点，不要试图面面俱到。

产品名称: {{product_name}}
竞品分析: {{competitor_analysis}}

# 分析要求
1. 提炼 1 个最核心、最能打动用户的差异化卖点。
2. 用一句朗朗上口、易于传播的广告语来概括这个卖点。
3. 解释为什么这个卖点能成为竞争优势。`,

    differentiation: `你是一位竞争战略顾问。请对该产品与主要竞品进行深度的差异化分析。

# 核心指令
1. **空值处理**：如果【竞品列表】为空，请选择该品类的 Top 3 标杆品牌作为假想敌。
2. **多维对比**：不要只比价格，要比价值、体验、服务和品牌调性。

产品名称: {{product_name}}
竞品列表: {{competitors}}

# 分析要求
1. **功能差异化**：我们有而别人没有的，或者我们做得更好的。
2. **情感差异化**：我们带给用户的独特感受。
3. **服务差异化**：售前、售中、售后的独特体验。
4. 明确指出我们的"护城河"在哪里。`,

    market_positioning: `你是一位资深的品牌战略家。请为该产品制定清晰、独特的市场定位策略。

# 核心指令
1. **空值处理**：如果【目标市场】为空，请默认以美国中产阶级消费市场为例。
2. **定位模型**：请运用 STP (Segmentation, Targeting, Positioning) 理论进行分析。

产品名称: {{product_name}}
目标市场: {{target_market}}

# 分析要求
1. **市场细分**：我们到底在切哪一块蛋糕？
2. **目标选择**：我们只服务哪一类人？
3. **定位声明**：用标准的定位句式描述（To [目标人群], [产品名] is the [品类] that [核心利益] because [信任状]）。`,

    visual_style: `你是一位获得红点设计大奖的视觉设计总监。请为该产品建议一套符合品牌调性的视觉风格方案。

# 核心指令
1. **空值处理**：如果【品牌调性】为空，请根据产品名称和类别推断适合的风格（如：科技产品偏冷淡风，家居产品偏温馨风）。
2. **具体可执行**：建议必须具体到颜色代码、字体类型和构图方式，方便设计师执行。

产品名称: {{product_name}}
品牌调性: {{brand_tone}}

# 分析要求
1. **主色调**：推荐 1 个主色和 2 个辅助色（附 Hex 代码）。
2. **图片风格**：描述产品摄影的光影、背景和道具选择。
3. **字体建议**：推荐适合的英文标题字体和正文字体。
4. **设计关键词**：给出 3 个指导设计的形容词（如：Minimalist, Organic, Bold）。`
};
